"use client"

import {
  IconBrandWhatsapp,
  IconExternalLink,
  IconPhone,
  IconPhoneCall,
  IconPhoneOff,
} from "@tabler/icons-react"
import { useTranslations } from "next-intl"

import { Button } from "@/components/ui/button"
import type { WhatsAppCallIncomingPayload } from "@/lib/whatsapp-calls-types"

interface WhatsAppIncomingCallPopupProps {
  call: WhatsAppCallIncomingPayload
  onAnswer: () => void
  onDecline: () => void
  /** True while the accept request is in flight (the claim race is server-side). */
  busy?: boolean
}

/**
 * Bottom-right ringing card for an inbound WhatsApp call.
 *
 * Deliberately a visual clone of `telephony/incoming-call-popup.tsx` — same
 * geometry, same three actions — repainted in WhatsApp green (#25D366 family)
 * with the brand mark, so an agent can tell at a glance which line is ringing
 * when both the PBX and WhatsApp popups can appear in the same corner.
 */
export function WhatsAppIncomingCallPopup({
  call,
  onAnswer,
  onDecline,
  busy = false,
}: WhatsAppIncomingCallPopupProps) {
  const t = useTranslations("whatsappCalls")

  const openLead = () => {
    if (call.lead?.id) {
      window.open(`/leads/${call.lead.id}`, "_blank")
    }
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-80 overflow-hidden rounded-xl border bg-background shadow-2xl">
      {/* Animated ring pulse bar — WhatsApp green */}
      <div className="h-1 animate-pulse bg-[#25D366]" />

      <div className="space-y-3 p-4">
        {/* Header */}
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#25D366]/15">
            <IconBrandWhatsapp className="h-5 w-5 animate-pulse text-[#128C7E] dark:text-[#25D366]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium uppercase tracking-wider text-[#128C7E] dark:text-[#25D366]">
              {t("incoming.title")}
            </p>
            <p className="truncate text-lg font-semibold">
              {call.lead?.full_name || call.from_number || t("incoming.unknownCaller")}
            </p>
          </div>
        </div>

        {/* Caller details */}
        <div className="space-y-1.5 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <IconPhone className="h-3.5 w-3.5" />
            <span>{call.from_number || t("incoming.unknownNumber")}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            className="flex-1 bg-[#25D366] text-white hover:bg-[#1FAF52]"
            onClick={onAnswer}
            disabled={busy}
          >
            <IconPhoneCall className="mr-1.5 h-4 w-4" />
            {t("incoming.answer")}
          </Button>
          <Button variant="outline" className="flex-1" onClick={onDecline} disabled={busy}>
            <IconPhoneOff className="mr-1.5 h-4 w-4" />
            {t("incoming.decline")}
          </Button>
          {call.lead?.id && (
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={openLead}
              aria-label={t("incoming.openLead")}
            >
              <IconExternalLink className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
